import React, { useState, useRef, useEffect } from 'react';
import type { PanelSize } from '../ToolExecution/types';

interface ResizableFocusedPanelProps {
  children: React.ReactNode;
  panelSize: PanelSize;
  defaultWidth?: number;
  minWidth?: number;
  maxWidth?: number;
  className?: string;
}

export const ResizableFocusedPanel: React.FC<ResizableFocusedPanelProps> =
  React.memo(
    ({
      children,
      panelSize,
      defaultWidth = 950, // Slightly larger default for main content
      minWidth = 600,
      maxWidth = 1400,
      className = '',
    }: ResizableFocusedPanelProps) => {
      const [width, setWidth] = useState(defaultWidth);
      const [isResizing, setIsResizing] = useState(false);
      const resizeRef = useRef<{ startX: number; startWidth: number } | null>(
        null,
      );

      useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
          if (!isResizing || !resizeRef.current) {
            return;
          }

          // For focused view (left panel), we resize from the right edge
          const delta = e.clientX - resizeRef.current.startX;
          const newWidth = Math.min(
            Math.max(resizeRef.current.startWidth + delta, minWidth),
            maxWidth,
          );

          requestAnimationFrame(() => {
            setWidth(newWidth);
          });
        };

        const handleMouseUp = () => {
          setIsResizing(false);
          resizeRef.current = null;
          document.body.style.cursor = '';
          document.body.style.userSelect = '';
        };

        if (isResizing) {
          document.body.style.cursor = 'ew-resize';
          document.body.style.userSelect = 'none';
          document.addEventListener('mousemove', handleMouseMove);
          document.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
          document.removeEventListener('mousemove', handleMouseMove);
          document.removeEventListener('mouseup', handleMouseUp);
          document.body.style.cursor = '';
          document.body.style.userSelect = '';
        };
      }, [isResizing, minWidth, maxWidth]);

      const handleResizeStart = (e: React.MouseEvent) => {
        setIsResizing(true);
        resizeRef.current = {
          startX: e.clientX,
          startWidth: width,
        };
      };

      return (
        <div
          className={`${
            panelSize === 'expanded'
              ? 'z-[100] border-r border-gray-200 dark:border-gray-700'
              : 'fixed inset-0 z-50 bg-white/95 backdrop-blur-sm dark:bg-gray-900/95'
          } flex flex-col bg-white dark:bg-gray-900 ${!isResizing ? 'transition-[width] duration-200' : ''} ${className}`}
          style={{
            width: panelSize === 'expanded' ? width : '100%',
            height: panelSize === 'expanded' ? '100%' : '100vh',
          }}
        >
          {/* Resize handle on the right side for focused view */}
          {panelSize === 'expanded' && (
            <div
              className="group absolute top-0 -right-2 z-50 h-full w-4 cursor-ew-resize hover:bg-[#FF732E]/5"
              onMouseDown={handleResizeStart}
            >
              {/* Line indicator */}
              <div
                className={`absolute top-0 left-1/2 h-full w-[2px] -translate-x-1/2 bg-gradient-to-r from-transparent via-gray-200 to-transparent ${
                  isResizing
                    ? '!via-[#FF732E]'
                    : 'group-hover:!via-[#FF732E]/40'
                }`}
              />
              {/* Middle resize indicator */}
              <div
                className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-md border border-gray-200/50 bg-white/80 p-1 opacity-0 shadow-sm transition-opacity duration-200 dark:border-gray-700/50 dark:bg-gray-900/80 ${
                  isResizing ? 'opacity-100' : 'group-hover:opacity-100'
                }`}
              >
                <svg
                  className={`h-4 w-4 ${
                    isResizing
                      ? 'text-[#FF732E]'
                      : 'text-gray-400 group-hover:text-[#FF732E] dark:text-gray-500'
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9"
                  />
                </svg>
              </div>
            </div>
          )}
          {children}
        </div>
      );
    },
  );

ResizableFocusedPanel.displayName = 'ResizableFocusedPanel';
